if (!customElements.get('whats-new-popup')) {
  customElements.define(
    'whats-new-popup',
    class WhatsNewPopup extends ModalElement {
      constructor() {
        super();

        if (window.location.pathname === '/challenge' || (typeof theme.cookiesEnabled === 'function' && !theme.cookiesEnabled())) {
          return;
        }

        if (!this.shouldDisplayOnViewport && !this.testMode && !this.submitted && !Shopify.designMode) {
          return;
        }

        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }

      connectedCallback() {
        super.connectedCallback();

        this.controls.forEach((control) => {
          control.addEventListener('click', () => this.cancelAutoOpen(), {
            capture: true,
            signal: this.abortController.signal
          });
        });

        this.querySelectorAll('[data-whats-new-complete]').forEach((link) => {
          link.addEventListener('click', this.remember.bind(this), { signal: this.abortController.signal });
        });
      }

      disconnectedCallback() {
        clearTimeout(this.loadTimer);
        this.stopConsentWait?.();
        this.newsletterObserver?.disconnect();
        super.disconnectedCallback();
      }

      onButtonClick(event) {
        event.preventDefault();
        this.cancelAutoOpen();

        if (this.open) {
          this.hide();
          return;
        }

        this.showWhenAvailable(event.currentTarget);
      }

      get shouldLock() {
        return true;
      }

      get focusElement() {
        return this.querySelector('.whats-new__close') || super.focusElement;
      }

      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 4;
      }

      get newsletterDelay() {
        return this.hasAttribute('data-newsletter-delay') ? parseInt(this.getAttribute('data-newsletter-delay')) : 7;
      }

      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 30;
      }

      get campaignKey() {
        return this.getAttribute('data-campaign-key') || 'default';
      }

      get howCookieName() {
        return `dilute-whats-new-v2-${encodeURIComponent(this.campaignKey)}`;
      }

      get newsletterCookieName() {
        return 'concept:newsletter-popup';
      }

      get hasHow() {
        return this.getAttribute('data-has-how') === 'true';
      }

      get hasNewsletter() {
        return this.getAttribute('data-has-newsletter') === 'true';
      }

      get showHowDesktop() {
        return this.getAttribute('data-show-how-desktop') === 'true';
      }

      get alwaysShow() {
        return this.getAttribute('data-always-show') === 'true';
      }

      // On by default, so the popup never blocks the cookie banner.
      get waitForConsent() {
        return this.getAttribute('data-wait-for-consent') !== 'false';
      }

      get shouldDisplayOnViewport() {
        return this.canShowHow || this.hasNewsletter;
      }

      get canShowHow() {
        return this.hasHow && (this.showHowDesktop || window.matchMedia(theme.config.mediaQuerySmall).matches);
      }

      get howDue() {
        return (this.canShowHow || this.testMode) && (this.alwaysShow || this.testMode || !this.getCookie(this.howCookieName));
      }

      get newsletterDue() {
        return this.hasNewsletter && (this.alwaysShow || this.testMode || !this.getCookie(this.newsletterCookieName));
      }

      get submitted() {
        return this.querySelector('.whats-new__newsletter-form .alert') !== null;
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;

        // A Shopify customer form response must stay visible even when the
        // campaign cookie has already been set.
        if (this.submitted) {
          this.load(1);
          return;
        }

        if (this.howDue || this.newsletterDue) {
          this.load(this.howDue ? this.delay : this.newsletterDelay);
        }
      }

      load(delay) {
        if (Shopify.designMode) return;

        clearTimeout(this.loadTimer);
        const startedAt = Date.now();

        // The delay counts from page load. If the visitor needed longer than
        // that to answer the cookie banner, wait one more second after it closes.
        this.whenCookieConsentHandled().then(() => {
          if (!this.isConnected || this.autoOpenCancelled) return;

          const remaining = Math.max(delay * 1000 - (Date.now() - startedAt), 1000);
          clearTimeout(this.loadTimer);
          this.loadTimer = setTimeout(() => this.showWhenAvailable(), remaining);
        });
      }

      cancelAutoOpen() {
        this.autoOpenCancelled = true;
        clearTimeout(this.loadTimer);
        this.stopConsentWait?.();
      }

      // Resolves once the visitor has answered the cookie banner, or right away
      // when no banner is needed. While the popup is open the rest of the page
      // is locked, so opening it on top of the banner would make the banner
      // impossible to click.
      whenCookieConsentHandled() {
        if (!this.waitForConsent) return Promise.resolve();

        return new Promise((resolve) => {
          let apiReady = false;
          let finished = false;
          let pollTimer = null;

          const finish = () => {
            if (finished) return;
            finished = true;
            clearInterval(pollTimer);
            document.removeEventListener('visitorConsentCollected', onConsentCollected);
            this.stopConsentWait = null;
            resolve();
          };

          const check = () => {
            if (this.cookieConsentHandled(apiReady)) finish();
          };

          const onConsentCollected = () => {
            apiReady = true;
            // Give the banner a moment to close before checking again.
            setTimeout(check, 300);
          };

          this.stopConsentWait = () => {
            finished = true;
            clearInterval(pollTimer);
            document.removeEventListener('visitorConsentCollected', onConsentCollected);
            this.stopConsentWait = null;
          };

          document.addEventListener('visitorConsentCollected', onConsentCollected);

          if (typeof window.Shopify?.loadFeatures === 'function') {
            try {
              window.Shopify.loadFeatures([{
                name: 'consent-tracking-api',
                version: '0.1',
                onLoad: () => {
                  apiReady = true;
                  check();
                }
              }]);
            }
            catch (error) {
              apiReady = true;
            }
          }
          else {
            apiReady = true;
          }

          pollTimer = setInterval(check, 1000);
          check();
        });
      }

      cookieConsentHandled(apiReady) {
        if (!apiReady || this.cookieBannerVisible()) return false;

        const privacy = window.Shopify?.customerPrivacy;
        if (!privacy) return true;

        try {
          if (typeof privacy.shouldShowBanner === 'function' && !privacy.shouldShowBanner()) {
            return true;
          }

          if (typeof privacy.currentVisitorConsent === 'function') {
            const consent = privacy.currentVisitorConsent() || {};
            if (['marketing', 'analytics', 'preferences'].some((key) => consent[key] === 'yes' || consent[key] === 'no')) {
              return true;
            }
          }

          if (typeof privacy.getTrackingConsent === 'function') {
            return privacy.getTrackingConsent() !== 'no_interaction';
          }
        }
        catch (error) {
          return true;
        }

        return true;
      }

      cookieBannerVisible() {
        return ['#shopify-pc__banner', '#shopify-pc__prefs'].some((selector) => {
          const element = document.querySelector(selector);
          if (!element) return false;

          const style = window.getComputedStyle(element);
          return style.display !== 'none'
            && style.visibility !== 'hidden'
            && element.getClientRects().length > 0;
        });
      }

      showWhenAvailable(activeElement = null) {
        if (activeElement) {
          this.pendingActiveElement = activeElement;
        }

        if (!this.isConnected || this.open || (!this.testMode && !this.shouldDisplayOnViewport)) return;

        // Automatic opening waits while the cookie banner is still on screen.
        // A visitor's own click on the popup button opens it right away.
        if (!this.pendingActiveElement && this.waitForConsent && this.cookieBannerVisible()) {
          this.loadTimer = setTimeout(() => this.showWhenAvailable(), 500);
          return;
        }

        const anotherModalOpen = Array.from(document.querySelectorAll('.x-modal, .drawer'))
          .some((modal) => modal !== this
            && !modal.hidden
            && (modal.hasAttribute('open') || modal.hasAttribute('inert')));

        if (anotherModalOpen) {
          this.loadTimer = setTimeout(() => this.showWhenAvailable(), 500);
          return;
        }

        const focusReturnElement = this.pendingActiveElement
          || (document.activeElement instanceof HTMLElement ? document.activeElement : null);

        this.pendingActiveElement = null;
        this.show(focusReturnElement);
      }

      afterHide() {
        this.newsletterObserver?.disconnect();
        this.newsletterObserver = null;
        super.afterHide();
        // The newsletter is part of the same popup, so closing it counts as
        // seen. Otherwise the popup would reopen later on the homepage.
        this.newsletterSeen = true;
        this.remember();
      }

      afterShow() {
        super.afterShow();
        clearTimeout(this.loadTimer);
        this.observeNewsletter();
        this.remember();

        if (this.submitted && !this.formResponseHandled) {
          this.formResponseHandled = true;
          setTimeout(() => this.focusFormResponse());
        }
      }

      focusFormResponse() {
        const alert = this.querySelector('.whats-new__newsletter-form .alert');
        if (!alert) return;

        const emailInput = this.querySelector('.whats-new__newsletter-form input[type="email"]');
        const focusTarget = alert.classList.contains('alert--error') && emailInput ? emailInput : alert;

        if (focusTarget === alert) {
          alert.setAttribute('tabindex', '0');
        }

        alert.scrollIntoView({ block: 'center' });
        theme.a11y.trapFocus(this, focusTarget);
      }

      observeNewsletter() {
        const newsletter = this.querySelector('.whats-new__newsletter');
        if (!newsletter || this.newsletterSeen) return;

        if (!('IntersectionObserver' in window)) {
          this.newsletterSeen = true;
          this.remember();
          return;
        }

        this.newsletterObserver?.disconnect();
        this.newsletterObserver = new IntersectionObserver((entries) => {
          const newsletterVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35);
          if (!newsletterVisible) return;

          this.newsletterSeen = true;
          this.newsletterObserver?.disconnect();
          this.newsletterObserver = null;
          this.remember();
        }, {
          root: this.querySelector('.drawer__content'),
          threshold: 0.35
        });

        this.newsletterObserver.observe(newsletter);
      }

      remember() {
        if (this.alwaysShow || this.testMode) {
          if (this.canShowHow || (this.testMode && this.hasHow)) {
            this.removeCookie(this.howCookieName);
          }

          if (this.hasNewsletter) {
            this.removeCookie(this.newsletterCookieName);
          }
          return;
        }

        if (this.canShowHow) {
          this.setCookie(this.howCookieName, this.expiry);
        }

        if (this.hasNewsletter && (this.newsletterSeen || this.submitted)) {
          this.setCookie(this.newsletterCookieName, this.expiry);
        }
      }

      getCookie(name) {
        const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const match = document.cookie.match(`(^|;)\\s*${escapedName}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }

      setCookie(name, expiry) {
        document.cookie = `${name}=true; max-age=${expiry * 24 * 60 * 60}; path=/; SameSite=Lax`;
      }

      removeCookie(name) {
        document.cookie = `${name}=; max-age=0; path=/`;
      }
    }
  );
}
